import { useEffect, useRef } from "react";
import { View, ActivityIndicator, StyleSheet } from "react-native";
import { router } from "expo-router";
import { supabase } from "@/lib/supabase-client";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { jwtDecode } from "jwt-decode";

type JwtClaims = {
  needs_password_update?: boolean;
  tenant_id?: string;
};

export default function AuthCallback() {
  const { colors } = useTheme();
  const handled = useRef(false);

  useEffect(() => {
    // Listen for auth state changes — Supabase fires PASSWORD_RECOVERY
    // specifically when a recovery token is exchanged
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (handled.current) return;

      if (event === "PASSWORD_RECOVERY" && session) {
        handled.current = true;
        router.replace("/update-password?recovery=true");
        return;
      }

      if (event === "SIGNED_IN" && session) {
        handled.current = true;

        try {
          const claims = jwtDecode<JwtClaims>(session.access_token);

          if (claims.needs_password_update && claims.tenant_id) {
            router.replace(`/new-password/${claims.tenant_id}`);
          } else {
            router.replace("/");
          }
        } catch (decodeError) {
          console.error("JWT decode error:", decodeError);
          router.replace("/");
        }
        return;
      }
    });

    // Fallback: if no auth event fires within 5s, redirect to sign-in
    const timeout = setTimeout(() => {
      if (!handled.current) {
        handled.current = true;
        console.error("Auth callback timed out");
        router.replace("/sign-in");
      }
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  return (
    <View style={styles.container}>
      <ActivityIndicator size="large" color={colors.primary} />
      <ThemedText style={styles.text}>Verifying...</ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  text: {
    marginTop: 16,
  },
});
